import './styles/globals.css';

import { Center, Heading, Icon, Stack, Text } from '@cortex/ui';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RouterProvider } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import { StrictMode, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';

import { isSupabaseConfigured } from '@/lib/supabase';
import { AuthProvider, useAuthContext } from '@/providers/auth-provider';
import { queryClient } from '@/providers/query-provider';
import { createAppRouter } from '@/router';

/**
 * Inner app component that has access to auth context
 * and passes it to the router.
 *
 * Creates the router ONCE and updates context on auth changes
 * to avoid remounting the entire route tree.
 */
function InnerApp() {
  const auth = useAuthContext();

  // Create router once with initial context
  const routerRef = useRef<ReturnType<typeof createAppRouter> | undefined>(
    undefined
  );
  if (!routerRef.current) {
    routerRef.current = createAppRouter({
      auth,
      queryClient,
    });
  }

  // Keep router context in sync with auth state
  useEffect(() => {
    routerRef.current?.update({
      context: { auth, queryClient },
    });
  }, [auth]);

  // Only re-evaluate route guards when auth state meaningfully changes
  useEffect(() => {
    routerRef.current?.invalidate();
  }, [auth.isAuthenticated, auth.isLoading]);

  return (
    <>
      <RouterProvider router={routerRef.current} />
      <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
    </>
  );
}

/**
 * Configuration error component
 */
function ConfigError() {
  return (
    <Center className="min-h-screen bg-gray-50 p-6">
      <Stack
        align="center"
        gap="md"
        className="max-w-md rounded-lg border border-red-200 bg-white p-8 shadow-lg"
      >
        <Center className="h-12 w-12 rounded-full bg-red-100">
          <Icon icon={AlertTriangle} size="lg" className="text-red-600" />
        </Center>
        <Heading level={1} className="text-xl font-semibold text-gray-900">
          Configuration Error
        </Heading>
        <Text size="sm" className="text-gray-600">
          Missing required environment variables. The app was built without
          Supabase configuration.
        </Text>
        <Stack
          gap="xs"
          className="w-full rounded bg-gray-100 p-3 text-left text-xs text-gray-700"
        >
          <Text className="font-mono">VITE_SUPABASE_URL</Text>
          <Text className="font-mono">VITE_SUPABASE_ANON_KEY</Text>
        </Stack>
        <Text size="xs" className="text-gray-500">
          Rebuild with the required build arguments to fix this issue.
        </Text>
      </Stack>
    </Center>
  );
}

/**
 * Root app component with all providers
 */
function App() {
  // Check if Supabase is configured before rendering the app
  if (!isSupabaseConfigured()) {
    return <ConfigError />;
  }

  return (
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <InnerApp />
        </AuthProvider>
      </QueryClientProvider>
    </StrictMode>
  );
}

// Mount the app
const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

createRoot(rootElement).render(<App />);
